import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { describeSchedule, fmtClock, fmtPacific, Friendly, fromSchedule, Mode, nextRuns, scheduleProblem, switchMode, Time, toSchedule, WINDOW_MINUTE_STEPS } from './scheduleModel';

const MODES: [Mode, string][] = [
  ['none', 'Not scheduled'],
  ['every', 'Every N minutes or hours'],
  ['daily', 'Daily at'],
  ['weekly', 'On these days at'],
  ['monthly', 'Monthly on day'],
  ['continuous', 'Whenever there is room'],
  ['cron', 'Advanced (cron)'],
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_x, h) => h);

const toInput = (t: Time) => `${String(t.h).padStart(2, '0')}:${String(t.m).padStart(2, '0')}`;
const fromInput = (v: string): Time | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v);
  return m ? { h: Number(m[1]), m: Number(m[2]) } : null;
};

function TimeField({ value, onChange, label = 'Time (PT)' }: { value: Time; onChange: (t: Time) => void; label?: string }) {
  return <TextField size="small" type="time" label={label} value={toInput(value)} onChange={e => { const t = fromInput(e.target.value); if (t) onChange(t); }} inputProps={{ step: 300 }} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />;
}

/**
 * An agent's schedule, picked in plain terms and stored as the string the hub reads (scheduleModel.ts). Shows what it
 * means in a sentence and the next five runs in Pacific, worked out the way the hub does.
 */
export default function ScheduleEditor({ value, onChange, lastStartedAt, disabled }: { value: string; onChange: (s: string) => void; lastStartedAt: Date | null; disabled?: boolean }) {
  const [f, setF] = useState<Friendly>(() => fromSchedule(value));
  // A value set from outside (Undo, Reset) replaces what's being edited; one this editor produced is already shown.
  useEffect(() => { if (toSchedule(f) !== value) setF(fromSchedule(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (next: Friendly) => { setF(next); onChange(toSchedule(next)); };

  const problem = scheduleProblem(value);
  const runs = problem ? [] : nextRuns(value, 5, new Date(), lastStartedAt);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Runs" value={f.mode} disabled={disabled} onChange={e => set(switchMode(f, e.target.value as Mode))} sx={{ minWidth: 230 }}>
          {MODES.map(([m, label]) => <MenuItem key={m} value={m}>{label}</MenuItem>)}
        </TextField>

        {f.mode === 'every' && (
          <>
            {f.window && f.unit === 'm' ? (
              <TextField select size="small" label="Every" value={WINDOW_MINUTE_STEPS.includes(f.n) ? f.n : ''} disabled={disabled} onChange={e => set({ ...f, n: Number(e.target.value) })} sx={{ width: 90 }}>
                {WINDOW_MINUTE_STEPS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </TextField>
            ) : (
              <TextField size="small" type="number" label="Every" value={f.n} disabled={disabled} onChange={e => set({ ...f, n: Math.max(1, Math.round(Number(e.target.value) || 1)) })} inputProps={{ min: 1 }} sx={{ width: 90 }} />
            )}
            <TextField select size="small" label="Unit" value={f.unit} disabled={disabled} onChange={e => set({ ...f, unit: e.target.value as 'm' | 'h', n: e.target.value === 'm' && f.window ? 15 : f.n })} sx={{ width: 110 }}>
              <MenuItem value="m">minutes</MenuItem>
              <MenuItem value="h">hours</MenuItem>
            </TextField>
            <FormControlLabel
              control={<Checkbox size="small" checked={!!f.window} disabled={disabled} onChange={e => set({ ...f, window: e.target.checked ? { from: 8, to: 18 } : null, n: e.target.checked && f.unit === 'm' && !WINDOW_MINUTE_STEPS.includes(f.n) ? 15 : f.n })} />}
              label="Only between"
            />
            {f.window && (
              <>
                <TextField select size="small" label="From" value={f.window.from} disabled={disabled} onChange={e => set({ ...f, window: { ...f.window!, from: Number(e.target.value) } })} sx={{ width: 120 }}>
                  {HOURS.map(h => <MenuItem key={h} value={h}>{fmtClock({ h, m: 0 })}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Through the hour of" value={f.window.to} disabled={disabled} onChange={e => set({ ...f, window: { ...f.window!, to: Number(e.target.value) } })} sx={{ width: 170 }}>
                  {HOURS.filter(h => h >= f.window!.from).map(h => <MenuItem key={h} value={h}>{fmtClock({ h, m: 0 })}</MenuItem>)}
                </TextField>
              </>
            )}
          </>
        )}

        {f.mode === 'daily' && f.times.map((t, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
            <TimeField value={t} onChange={nt => set({ ...f, times: f.times.map((x, j) => (j === i ? nt : x)) })} label={i ? 'and at' : 'Time (PT)'} />
            {f.times.length > 1 && <Button size="small" disabled={disabled} onClick={() => set({ ...f, times: f.times.filter((_x, j) => j !== i) })}>Remove</Button>}
          </Box>
        ))}
        {f.mode === 'daily' && <Button size="small" disabled={disabled} onClick={() => set({ ...f, times: [...f.times, { h: 16, m: 0 }] })}>Add a time</Button>}

        {f.mode === 'weekly' && (
          <>
            <ToggleButtonGroup size="small" value={f.days} disabled={disabled} onChange={(_e, days: number[]) => days.length && set({ ...f, days })}>
              {DAYS.map((d, i) => <ToggleButton key={d} value={i} sx={{ px: 1.25, py: 0.5 }}>{d}</ToggleButton>)}
            </ToggleButtonGroup>
            <TimeField value={f.time} onChange={time => set({ ...f, time })} />
          </>
        )}

        {f.mode === 'monthly' && (
          <>
            <TextField select size="small" label="Day" value={f.day} disabled={disabled} onChange={e => set({ ...f, day: Number(e.target.value) })} sx={{ width: 90 }}>
              {Array.from({ length: 31 }, (_x, i) => i + 1).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TimeField value={f.time} onChange={time => set({ ...f, time })} />
          </>
        )}

        {f.mode === 'cron' && (
          <TextField
            size="small"
            label="Cron (Pacific)"
            value={f.text}
            disabled={disabled}
            onChange={e => set({ mode: 'cron', text: e.target.value })}
            sx={{ minWidth: 320, flex: 1, maxWidth: 520 }}
            helperText='minute hour day-of-month month day-of-week; several separated by ";"'
          />
        )}
      </Box>

      {problem ? (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>The hub could not read this: {problem}</Typography>
      ) : (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="body2">{describeSchedule(value)}</Typography>
          {f.mode === 'monthly' && f.day > 28 && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Months without a day {f.day} are skipped.</Typography>}
          {runs.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Next runs (PT){f.mode === 'every' && !f.window ? ', at the earliest' : ''}: {runs.map(fmtPacific).join(' · ')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
